/**
 * REF-2 — GET /sales/live/v2/refund-log → `RefundRecord`.
 *
 * The real endpoint is flatter than the FE's own component-vector model. This mapper is
 * the ONE place that gap is bridged, so every consumer downstream (Refund Log table, Refund
 * Reasons, Refunds by Staff, the context strip) reads a normal `RefundRecord` and never has
 * to know the difference. Three approximations remain, called out so they're easy to revisit
 * if the API grows richer:
 *
 *   1. `tipsAndGratRefunded` is Tips AND Gratuity combined — the API doesn't split them.
 *      The whole amount is put under `tip`; `gratuity` stays 0. Anything reading the two
 *      SEPARATELY (the Gratuity section of the Staff Tips ranking) will under-net.
 *   2. No channel-group classification — derived from the free-text `channel` string via
 *      the same third-party detection Order Log already uses.
 *   3. No per-tender breakdown and no tip-out-exposure flag — `tenders` is a single
 *      synthetic "Original tender" entry for exactly `refunded`, and
 *      `refundedAfterTipOut` is always undefined. The Refund History drawer's "Returned to"
 *      section and REF-6's amber tip-out icon will not reflect real refunds until the API
 *      carries this.
 *
 * Convenience Fee, Delivery Fee and Service Charge now arrive as their own real fields
 * (`convenienceFeeRefunded` / `deliveryRefunded` / `serviceChargeRefunded`) — read directly
 * below, replacing the old channel-based residual guess.
 */
import type { RefundLogItem } from '../../../interface/clickHouseInterface';
import { isThirdPartyChannel } from '../components/OrderLog/orderLogRow';
import { reasonGroupOf, NO_REASON } from './refundReasons';
import {
    deriveRefundType,
    emptyComponents,
    type RefundChannelGroup,
    type RefundRecord,
    type RefundType,
} from './refundModel';

const n = (v: number | null | undefined): number => (typeof v === 'number' && Number.isFinite(v) ? v : 0);
const r2 = (v: number): number => Number(v.toFixed(2));

function channelSlot(channel: string): { group: RefundChannelGroup; fulfillment: string; thirdParty: boolean } {
    const raw = String(channel ?? '').trim();
    if (isThirdPartyChannel(raw)) return { group: 'MARKETPLACE', fulfillment: 'Delivery', thirdParty: true };
    const key = raw.toLowerCase();
    if (/pickup/.test(key)) return { group: 'IN_STORE', fulfillment: 'Pickup', thirdParty: false };
    if (/dine.?in/.test(key)) return { group: 'IN_STORE', fulfillment: 'Dine-In', thirdParty: false };
    if (/deliver/.test(key)) return { group: 'DIRECT_ONLINE', fulfillment: 'Delivery', thirdParty: false };
    return { group: 'IN_STORE', fulfillment: raw || 'In-Store', thirdParty: false };
}

const TYPE_MAP: Record<string, RefundType> = { full: 'full', item: 'item', fee: 'fee' };

export function refundLogItemToRecord(item: RefundLogItem, index: number): RefundRecord {
    const items = r2(n(item.itemsRefunded));
    const tax = r2(n(item.taxRefunded));
    const tipsAndGrat = r2(n(item.tipsAndGratRefunded));
    const total = r2(n(item.refunded));
    const slot = channelSlot(item.channel);

    const components = {
        ...emptyComponents(),
        item: items,
        tax,
        tip: tipsAndGrat, // approximation #1 — combined tips + gratuity
        gratuity: 0,
        convenienceFee: r2(n(item.convenienceFeeRefunded)),
        deliveryFee: r2(n(item.deliveryRefunded)),
        serviceCharge: r2(n(item.serviceChargeRefunded)),
        cardProcessingFee: r2(n(item.cardProcessingFeeRefunded)),
    };

    const asserted = TYPE_MAP[String(item.refundType ?? '').trim().toLowerCase()];
    const staffName = String(item.staffName ?? '').trim() || 'Unassigned';
    const refundedAt = item.refundDate || item.orderDate;
    const stamp = `${refundedAt}T00:00:00Z`;
    // The API sends an empty string until a reason is captured on the refund.
    const reason = String(item.reason ?? '').trim() || NO_REASON;

    return {
        id: `refund-log-${item.orderNo}-${index}`,
        // The real order id (opens the Order Detail panel); '' when the backend doesn't send one.
        orderId: String(item.orderId ?? '').trim(),
        orderNo: item.orderNo,
        orderDate: item.orderDate,
        refundedAt,
        type: deriveRefundType(components, asserted),
        components,
        reason,
        reasonGroup: reasonGroupOf(item.reason),
        staffName,
        // REF-6 — the API doesn't distinguish who OWNED the check from who processed the
        // refund, so the refunder stands in for the check owner.
        checkOwner: staffName,
        // REF-5 approximation #4 — no per-tender breakdown from this endpoint.
        tenders: total > 0 ? [{ tender: 'Original tender', amount: total }] : [],
        channel: item.channel,
        channelGroup: slot.group,
        fulfillment: slot.fulfillment,
        customerName: null,
        salesSource: slot.thirdParty ? 'thirdparty' : 'direct',
        refundedAfterTipOut: undefined,
        createdAt: stamp,
        updatedAt: stamp,
    };
}

export function refundLogItemsToRecords(items: RefundLogItem[]): RefundRecord[] {
    return (items ?? []).map((item, i) => refundLogItemToRecord(item, i));
}
